// THE COVERAGE PAGE'S VIEW MODEL — one worklist across every source, plus the
// collapsed healthy summary. PURE (`now` injected, no I/O, no formatting).
//
// Brief: _architect/briefs/2026-09-15-coverage-page-overhaul.md §4, D9e step 5
//
// ⚠️⚠️ THE PAGE ANSWERS ONE QUESTION: WHAT NEEDS ME RIGHT NOW? Four sources'
// worth of correctly-modelled health is the INPUT, not the output. The model
// distinguishes delivery from activity, jobs from accounts, causes from
// symptoms — and none of that belongs on screen. What belongs on screen is red
// and amber at the top with what to do about them, and everything else collapsed
// into one green line per source.
//
// ⚠️ SO THERE IS NO SOURCE GROUPING IN THE WORKLIST. A reader with a dead Meta
// ad account and a stalled Google transfer has two problems, not two sections;
// sorting them by severity puts the worse one first regardless of which pipeline
// it came from. Sources reappear only in the HEALTHY summary, where "Meta — 231
// accounts, current" is the whole point.

import type { SourceHealth, HealthSourceKey } from '../../types/sourceHealth.js';
import { jobStatus } from './status.js';
import { isSourceStale } from './grid.js';
import { buildWorklist, countAccountsNeedingAttention, type WorklistEntry } from './worklist.js';
import type { HealthStatus } from './status.js';
import { STATUS_RANK } from './status.js';

export const SOURCE_LABEL: Record<HealthSourceKey, string> = {
  meta: 'Meta',
  crm: 'CRM',
  googleAds: 'Google Ads',
};

/**
 * One line in Needs attention.
 *
 * ⚠️ `action` IS A SENTENCE, NOT A BUTTON. Most of these have no one-click fix —
 * a stalled Google Ads transfer is repaired in the Cloud Console, not here — and
 * rendering a button that does nothing is worse than a sentence that says where
 * to go. Where a real trigger exists (`href`), the UI may link to it.
 */
export interface AttentionRow {
  key: string;
  source: HealthSourceKey;
  status: HealthStatus;
  /** What the row is: an account name, or a cause. */
  title: string;
  /** What is wrong, in one sentence. */
  reason: string;
  /** What to do about it. */
  action: string;
  href: string | null;
  /** >1 only for a collapsed cause. The UI shows the members behind it. */
  affectedCount: number;
  /** Named accounts behind a cause row, for the expander. Empty for a plain row. */
  affected: string[];
}

/** One collapsed green line. */
export interface HealthySummary {
  source: HealthSourceKey;
  label: string;
  /** Accounts NOT needing attention. */
  healthyAccounts: number;
  totalAccounts: number;
  /** The newest delivery across this source's jobs, or null. */
  lastRunAt: string | null;
  /** Set when the source itself is in trouble rather than any account. */
  note: string | null;
}

export interface HealthPageModel {
  attention: AttentionRow[];
  healthy: HealthySummary[];
  /** ⚠️ ACCOUNTS, not rows — a cause row of fourteen is fourteen accounts. */
  accountsNeedingAttention: number;
  /** True when there is genuinely nothing wrong. Drives the plain all-clear. */
  allClear: boolean;
}

/**
 * ⚠️ THE ACTION IS DERIVED FROM THE VERDICT, not stored on it. The status core
 * decides what is wrong; this decides what to do, and keeping them apart is what
 * lets a new rule get an action without the rule knowing about the page.
 *
 * ⚠️ NEVER INVENTS A CAPABILITY. Only Meta has a one-click backfill
 * (`POST /api/reporting/backfills`, surfaced on the Coverage page); CRM and
 * Google Ads are repaired elsewhere, and their rows say where rather than
 * offering a control that does not exist.
 */
function accountAction(source: HealthSourceKey, reason: string): { action: string; href: string | null } {
  const neverLoaded = /never loaded/i.test(reason);
  if (source === 'meta') {
    return {
      action: neverLoaded
        ? 'Run a backfill for this account from the Coverage page.'
        : 'Check the account is still connected, then backfill from the Coverage page.',
      href: '/reporting/coverage',
    };
  }
  if (source === 'crm') {
    return {
      action: neverLoaded
        ? 'Check the club is linked and enabled, then run the CRM sync.'
        : 'Check the CRM sync is reaching this club — see its last run.',
      href: null,
    };
  }
  return {
    action: neverLoaded
      ? 'Check the campaign-name match token resolves this account.'
      : 'Check the Google Ads transfer for this account in the Cloud Console.',
    href: null,
  };
}

function jobAction(source: HealthSourceKey): { action: string; href: string | null } {
  if (source === 'meta') {
    return { action: 'Check the pull job — see Sync Health for its last runs.', href: '/reporting/sync-health' };
  }
  if (source === 'crm') {
    return { action: 'Check the CRM sync job ran on schedule.', href: null };
  }
  return {
    action: 'Check this MCC transfer in the BigQuery Data Transfer console.',
    href: null,
  };
}

/**
 * ⚠️⚠️ A JOB ROW APPEARS ONLY WHEN THE JOB ITSELF IS IN TROUBLE, and is SEPARATE
 * from the accounts behind it. A dead job also makes its accounts stale, and both
 * would otherwise be listed — the job row naming the cause and fourteen account
 * rows repeating it. The account rows collapse into a cause row of their own
 * (step 3), so what the reader sees is at most two lines for one outage: the job,
 * and the accounts it took out.
 */
export function buildHealthPage(
  sources: readonly SourceHealth[],
  nowMs: number,
): HealthPageModel {
  const attention: AttentionRow[] = [];
  const healthy: HealthySummary[] = [];

  for (const src of sources) {
    const key = src.expectation.source;
    const label = SOURCE_LABEL[key];

    for (const job of src.jobs) {
      const v = jobStatus(job, src.expectation, nowMs);
      if (v.status === 'green') continue;
      const { action, href } = jobAction(key);
      attention.push({
        key: `job:${job.jobKey}`,
        source: key,
        status: v.status,
        title: job.jobLabel,
        reason: v.reason ?? 'This job needs checking.',
        action,
        href,
        affectedCount: job.accountsBehind,
        affected: [],
      });
    }

    // ⚠️⚠️ THE SAME SUPPRESSION AS THE GRID (§5). This page already renders the
    // JOB's own verdict above — so without this, a stale pull produced a job row
    // AND an account row for every account it explains, which is the 234-row
    // wallpaper one surface over.
    const entries: WorklistEntry[] = buildWorklist(
      src.accounts,
      src.expectation,
      nowMs,
      isSourceStale(src, nowMs),
    );
    for (const e of entries) {
      if (e.kind === 'group') {
        const { action, href } = accountAction(key, e.reason ?? '');
        attention.push({
          key: `cause:${e.causeKey}`,
          source: key,
          status: e.status,
          title: e.causeLabel,
          reason: e.reason ?? 'These accounts need checking.',
          action,
          href,
          affectedCount: e.memberCount,
          affected: e.members.map((m) => m.accountName),
        });
      } else {
        const { action, href } = accountAction(key, e.reason ?? '');
        attention.push({
          key: `account:${key}:${e.account.accountId}`,
          source: key,
          status: e.status,
          title: e.account.accountName,
          reason: e.reason ?? 'This account needs checking.',
          action,
          href,
          affectedCount: 1,
          affected: [],
        });
      }
    }

    // ⚠️ Every unattributed group is its own row and is NEVER folded into an
    // account. On a shared Google customer that spend may belong to another
    // client entirely.
    for (const u of src.unattributed) {
      attention.push({
        key: `unattributed:${key}:${u.label}`,
        source: key,
        status: 'amber',
        title: u.label,
        reason: u.detail,
        action: 'Resolve the mapping so this data reaches an account.',
        href: null,
        affectedCount: u.itemCount,
        affected: [],
      });
    }

    const needing = countAccountsNeedingAttention(entries);
    const lastRunAt = src.jobs.reduce<string | null>(
      (acc, j) => (j.lastRunAt !== null && (acc === null || j.lastRunAt > acc) ? j.lastRunAt : acc),
      null,
    );
    healthy.push({
      source: key,
      label,
      healthyAccounts: src.accounts.length - needing,
      totalAccounts: src.accounts.length,
      lastRunAt,
      // ⚠️ A source with NO accounts is not healthy, it is unreported — and a
      // green line reading "0 accounts, current" would be the hardcoded-green
      // failure in its quietest form.
      note: src.accounts.length === 0 ? 'No accounts are reporting for this source.' : null,
    });
  }

  return {
    attention: sortAttention(attention),
    healthy: healthy.sort((a, b) => a.label.localeCompare(b.label)),
    accountsNeedingAttention: attention.reduce((n, r) => n + r.affectedCount, 0),
    allClear: attention.length === 0,
  };
}

/**
 * Worst first, then widest blast radius, then stable by title.
 *
 * ⚠️ DELIBERATELY NOT GROUPED BY SOURCE. The reader's question is "what needs
 * me", and a red CRM row belongs above an amber Meta one however the pipelines
 * are organised internally.
 */
export function sortAttention(rows: readonly AttentionRow[]): AttentionRow[] {
  return [...rows].sort((a, b) => {
    const s = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (s !== 0) return s;
    const w = b.affectedCount - a.affectedCount;
    if (w !== 0) return w;
    return a.title.localeCompare(b.title);
  });
}
